// src/hooks/useAuth.js
import { useCallback } from "react";
import useCancelableDebouncedApi from "./useCancelableDebouncedApi";
import { authApi } from "service/api";
import useLocalStorage from "./useLocalStorage";
import { useDispatch } from "react-redux";
import { cacheCurrentUser } from "store/slices";
import { setCurrentUserData } from "service/mapper";
import { useAlerts } from "contexts/AlertsContext";
import { logger, normalizeEmail } from "utils";
import { useTranslation } from "react-i18next";

/** Which signup codes are asked for: "both", "phone", "email" or undefined */
export const signupOtpType = ({ isPhoneVerified, isEmailVerified } = {}) =>
  isPhoneVerified && isEmailVerified
    ? "both"
    : isPhoneVerified
      ? "phone"
      : isEmailVerified
        ? "email"
        : undefined;

const useAuth = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [accessToken, setAccessToken] = useLocalStorage("access_token", null);
  const [userSession, setUserSession] = useLocalStorage("userSession", null);
  const [isVerify, setIsVerify] = useLocalStorage("verify", null);
  const { callApi } = useCancelableDebouncedApi();
  const { showAlerts } = useAlerts();
  const setVerify = async (data) => {
    setIsVerify(data);
  };

  const clearSession = useCallback(() => {
    setUserSession(null);
  }, [setUserSession]);

  const signup = useCallback(
    async (details) => {
      // the email is always sent in lowercase
      const credentials = { ...details, email: normalizeEmail(details.email) };
      try {
        const res = await callApi(() => authApi.signup(credentials));
        if (!res.error) {
          const { UserSub} = res.data.auth_detail;
          console.log("res.data",res.data.skip_phone_otp_verification, res.data);
          setUserSession({
            email: credentials.email,
            USERNAME: UserSub,
            isPhoneVerified: !res.data.skip_phone_otp_verification,
            isEmailVerified: true,
          });
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res);
        }
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [callApi, setUserSession],
  );

  const signupVerify = useCallback(
    async (emailOtp, phoneOtp) => {
      try {
        const { isPhoneVerified, isEmailVerified, USERNAME } =
          userSession ?? {};

        const formData = {
          user_sub: USERNAME,
          ...(isPhoneVerified && { phone_otp: phoneOtp }),
          ...(isEmailVerified && { email_otp: emailOtp }),
          type: signupOtpType({ isPhoneVerified, isEmailVerified }),
        };
        const res = await callApi(() => authApi.signupVerify(formData));
        if (!res.error) {
          const { auth_detail, user_detail } = res.data;
          dispatch(cacheCurrentUser(setCurrentUserData(user_detail)));
          setIsVerify(true);
          setAccessToken({
            token: auth_detail.AccessToken,
            refresh: auth_detail.RefreshToken,
            username: userSession.USERNAME,
          });
          setUserSession(null);
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res);
        }
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [
      callApi,
      userSession,
      setUserSession,
      setAccessToken,
      dispatch,
      setIsVerify,
    ],
  );

  const updateSession = useCallback(
    (obj) => {
      setUserSession({ ...userSession, ...obj });
    },
    [setUserSession, userSession],
  );

  const login = useCallback(
    async (details) => {
      // the email is always sent in lowercase
      const credentials = { ...details, email: normalizeEmail(details.email) };
      try {
        const res = await callApi(() => authApi.login(credentials));
        if (!res.error) {
          const { ChallengeParameters, Session } = res.data;
          setUserSession({
            ...ChallengeParameters,
            session: Session,
            // "Resend" on the PIN page logs in again with it
            email: credentials.email,
          });
          return Promise.resolve(res.data);
        } else {
          return Promise.reject(res);
        }
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [callApi, setUserSession],
  );
  const loginVerify = useCallback(
    async (otp) => {
      if (!otp || otp.length === 0) {
        return Promise.reject(new Error("OTP is required"));
      }

      try {
        const formData = {
          otp,
          user_sub: userSession?.USERNAME,
        };

        // ✅ Call API
        const res = await callApi(() => authApi.loginVerify(formData));

        if (res?.error) {
          // ✅ Normalize error handling
          throw res;
        }

        const { auth_detail, user_detail } = res?.data || {};

        if (!auth_detail?.AccessToken || !auth_detail?.RefreshToken) {
          throw new Error(
            "Invalid authentication details received from server",
          );
        }

        // ✅ Save user info
        dispatch(cacheCurrentUser(setCurrentUserData(user_detail)));

        // ✅ Save tokens
        setAccessToken({
          token: auth_detail.AccessToken,
          refresh: auth_detail.RefreshToken,
          username: userSession?.USERNAME,
        });

        // ✅ Clear temporary session
        setUserSession(null);

        // ✅ Return useful data
        return res.data;
      } catch (error) {
        if (error?.status === 410 || !!error?.response?.data?.data?.session) {
          updateSession({ session: error?.response?.data?.data?.session });
        } else if (error?.status === 401) {
          const { email } = userSession;
          login({ email });
        }

        return Promise.reject(error);
      }
    },
    [
      callApi,
      userSession,
      setUserSession,
      setAccessToken,
      dispatch,
      updateSession,
      login,
    ],
  );

  const refreshOtp = useCallback(
    async ({ type }) => {
      try {
        const data = {
          type: type,
          user_sub: userSession?.USERNAME,
        };
        const res = await callApi(() => authApi.refreshOtp(data));
        if (!res.error) {
          if (type === "email") {
            // Handle email OTP refresh
            showAlerts(t("aNewEmailOtpHas"), { type: "success" });
          } else if (type === "phone") {
            // Handle phone OTP refresh
            showAlerts(t("aNewPhoneOtpHas"), { type: "success" });
          } else if (type === "both") {
            // Handle both OTPs refresh
            showAlerts(t("newOtpsHaveBeenSent"), {
              type: "success",
            });
          }
          return res.data;
        } else {
          return Promise.reject(res);
        }
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [t, callApi, showAlerts, userSession],
  );

  const removeAccessToken = useCallback(() => {
    setAccessToken(null);
  }, [setAccessToken]);

  // Signs out locally even when the logout request fails (offline, expired
  // token, ...): the user must never stay signed in after "Logout"
  const logout = useCallback(async () => {
    try {
      await callApi(() => authApi.logout());
    } catch (error) {
      logger.error("Error during logout:", error);
    } finally {
      removeAccessToken();
    }
  }, [callApi, removeAccessToken]);

  return {
    isAuthenticated: !!accessToken,
    signup,
    isVerify,
    setVerify,
    login,
    logout,
    loginVerify,
    signupVerify,
    accessToken,
    userSession,
    refreshOtp,
    clearSession,
    removeAccessToken,
    updateSession,
  };
};

export default useAuth;
